import React, { useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import axios from "axios";
import { toast } from "react-toastify";
import API_BASE_URL from "../../../config/api";

// ===== Animations =====
const floatIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px) scale(0.98);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

// ===== Styled components =====
const Section = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  padding: 120px 24px 100px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background: radial-gradient(circle at top left, #00c6ff 0, #062c3f 40%, #020617 90%);
  font-family: "Poppins", sans-serif;

  &::before {
    content: "";
    position: absolute;
    inset: -40%;
    background: radial-gradient(circle at 10% 0%, rgba(56, 189, 248, 0.15), transparent 55%),
      radial-gradient(circle at 90% 100%, rgba(94, 234, 212, 0.12), transparent 55%);
    opacity: 0.9;
    pointer-events: none;
  }
`;

const BackgroundOrb = styled.div`
  position: absolute;
  width: 420px;
  height: 420px;
  border-radius: 999px;
  background: radial-gradient(circle at 30% 20%, #ffffff, rgba(15, 23, 42, 0.2));
  top: -120px;
  right: -80px;
  opacity: 0.16;
  filter: blur(2px);
  pointer-events: none;
`;

const FormShell = styled.div`
  position: relative;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 40px 38px 38px;
  border-radius: 28px;
  background:
    linear-gradient(135deg, #040715, #020617) padding-box,
    linear-gradient(135deg, #22c1c3, #3b82f6, #8b5cf6) border-box;
  border: 1px solid transparent;
  box-shadow: 0 32px 90px rgba(15, 23, 42, 0.95);
  backdrop-filter: blur(26px);
  -webkit-backdrop-filter: blur(26px);
  color: #f9fafb;
  z-index: 1;
  animation: ${floatIn} 0.6s ease-out both;
`;

const HeaderRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 28px;

  @media (min-width: 768px) {
    flex-direction: row;
    align-items: flex-end;
    justify-content: space-between;
  }
`;

const FormTitle = styled.h2`
  font-size: 2.4rem;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #e5f2ff;
  margin: 0;

  span {
    color: #00cbf1;
  }

  @media (max-width: 576px) {
    font-size: 1.9rem;
  }
`;

const Underline = styled.div`
  width: 160px;
  height: 3px;
  border-radius: 999px;
  margin-top: 14px;
  background: linear-gradient(90deg, #22c1c3, #3b82f6, #8b5cf6);
`;

const SubText = styled.p`
  max-width: 420px;
  font-size: 0.95rem;
  line-height: 1.8;
  color: #cbd5f5;
  margin: 8px 0 0;
  text-align: left;
`;

const FormElement = styled.form`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 22px 24px;
  margin-top: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
`;

const Label = styled.label`
  margin-bottom: 8px;
  font-weight: 600;
  color: #e5e7eb;
  font-size: 0.9rem;
`;

const Input = styled.input`
  padding: 14px 14px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.7);
  color: #e5e7eb;
  font-size: 0.95rem;
  outline: none;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    border-color: #38bdf8;
    background: rgba(15, 23, 42, 0.9);
    box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.4);
  }

  @media (max-width: 476px) {
    width: 100%;
  }
`;

const TextArea = styled.textarea`
  padding: 14px 14px;
  border-radius: 12px;
  border: 1px solid rgba(148, 163, 184, 0.6);
  background: rgba(15, 23, 42, 0.7);
  color: #e5e7eb;
  font-size: 0.95rem;
  outline: none;
  resize: vertical;
  min-height: 140px;
  transition: border-color 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    border-color: #38bdf8;
    background: rgba(15, 23, 42, 0.9);
    box-shadow: 0 0 0 1px rgba(56, 189, 248, 0.4);
  }
`;

const FileInput = styled(Input).attrs({ type: "file" })`
  padding: 10px;
  cursor: pointer;
  background: rgba(15, 23, 42, 0.9);

  &::-webkit-file-upload-button {
    padding: 8px 14px;
    margin-right: 10px;
    border-radius: 999px;
    border: none;
    background: linear-gradient(90deg, #22c1c3, #3b82f6);
    color: #f9fafb;
    font-weight: 600;
    cursor: pointer;
  }
`;

const SubmitRow = styled.div`
  grid-column: 1 / -1;
  margin-top: 6px;
`;

const SubmitButton = styled.button`
  width: 100%;
  padding: 16px;
  border-radius: 999px;
  border: none;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #ffffff;
  cursor: pointer;
  background-image: linear-gradient(90deg, #22c55e, #10b981, #3b82f6);
  background-size: 200% 100%;
  box-shadow: 0 18px 40px rgba(15, 118, 110, 0.7);
  transition: background-position 0.35s ease, transform 0.15s ease, box-shadow 0.25s ease;
  text-transform: none;

  &:hover {
    background-position: 100% 0;
    transform: translateY(-2px);
    box-shadow: 0 24px 60px rgba(15, 118, 110, 0.95);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 12px 30px rgba(15, 118, 110, 0.8);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
    box-shadow: none;
  }
`;

// ===== Component =====
const CareerForm = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
    resume: null,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, files } = e.target;
    if (name === "resume") {
      setFormData({ ...formData, resume: files[0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    const { name, email, phone, message, resume } = formData;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const phoneRegex = /^\d{10,15}$/;
    const MAX_FILE_SIZE = 5 * 1024 * 1024;
    const supportedFileTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (name.trim() === "" || name.length < 3) {
      toast.error("Please enter a valid name (at least 3 characters).");
      setIsSubmitting(false);
      return;
    }
    if (!emailRegex.test(email)) {
      toast.error("Please enter a valid email address.");
      setIsSubmitting(false);
      return;
    }
    if (!phoneRegex.test(phone)) {
      toast.error("Please enter a valid phone number (only digits, 10–15 characters).");
      setIsSubmitting(false);
      return;
    }
    if (message.trim() === "" || message.length < 10) {
      toast.error("Please enter a valid message (at least 10 characters).");
      setIsSubmitting(false);
      return;
    }
    if (!resume) {
      toast.error("Please upload your resume.");
      setIsSubmitting(false);
      return;
    }
    if (!supportedFileTypes.includes(resume.type)) {
      toast.error("Invalid file type. Only PDF, DOC, and DOCX are allowed.");
      setIsSubmitting(false);
      return;
    }
    if (resume.size > MAX_FILE_SIZE) {
      toast.error("File size too large. Please upload a resume smaller than 5MB.");
      setIsSubmitting(false);
      return;
    }

    const form = new FormData();
    form.append("name", name);
    form.append("email", email);
    form.append("phone", phone);
    form.append("message", message);
    form.append("resume", resume);

    try {
      await axios.post(`${API_BASE_URL}/api/career.php`, form, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        message: "",
        resume: null,
      });
      toast.success("Application submitted successfully!");
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      toast.error("There was an error submitting the form.");
      console.error(error);
    }

    setIsSubmitting(false);
  };

  return (
    <Section>
      <BackgroundOrb />

      <FormShell>
        <HeaderRow>
          <div>
            <FormTitle>
              Career <span>Application Form</span>
            </FormTitle>
            <Underline />
          </div>
          <SubText>
            Tell us a bit about yourself and attach your resume. Our team will review
            your profile and get in touch if there&apos;s a fit.
          </SubText>
        </HeaderRow>

        <FormElement onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="name">Full Name</Label>
            <Input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter your full name"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter your email address"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              placeholder="Enter your phone number"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="message">Message</Label>
            <TextArea
              id="message"
              name="message"
              rows="6"
              value={formData.message}
              onChange={handleChange}
              placeholder="Write your message here"
              required
            />
          </FormGroup>

          <FormGroup>
            <Label htmlFor="resume">Upload Resume</Label>
            <FileInput
              id="resume"
              name="resume"
              accept=".pdf,.doc,.docx"
              onChange={handleChange}
              ref={fileInputRef}
              required
            />
          </FormGroup>

          <SubmitRow>
            <SubmitButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Submitting..." : "Submit Application"}
            </SubmitButton>
          </SubmitRow>
        </FormElement>
      </FormShell>
    </Section>
  );
};

export default CareerForm;
